'use client';

/**
 * Hook para disparar eventos de Meta Pixel
 * Usa el mismo event_id que se envía al backend para deduplicación
 */
export function useMetaPixel() {

    const isPixelLoaded = () => {
        return typeof window !== 'undefined' && typeof window.fbq === 'function';
    };

    /**
     * Genera un ID único para deduplicación cliente/servidor
     */
    const generateEventId = (prefix: string) => {
        return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    /**
     * Evento ViewContent - Cuando el usuario ve un evento/producto
     */
    const trackViewContent = (params: {
        eventId?: string;
        contentId: string;
        contentName: string;
        value?: number;
        currency?: string;
    }) => {
        const eventId = params.eventId || generateEventId('vc');

        if (isPixelLoaded()) {
            window.fbq('track', 'ViewContent', {
                content_ids: [params.contentId],
                content_name: params.contentName,
                content_type: 'product',
                value: params.value || 0,
                currency: params.currency || 'ARS',
            }, { eventID: eventId });
        }

        return eventId;
    };

    /**
     * Evento InitiateCheckout - Cuando el usuario inicia el proceso de compra
     */
    const trackInitiateCheckout = (params: {
        eventId?: string;
        value: number;
        currency?: string;
        numItems: number;
        contentIds?: string[];
    }) => {
        const eventId = params.eventId || generateEventId('ic');

        if (isPixelLoaded()) {
            window.fbq('track', 'InitiateCheckout', {
                value: params.value,
                currency: params.currency || 'ARS',
                num_items: params.numItems,
                content_ids: params.contentIds || [],
                content_type: 'product',
            }, { eventID: eventId });
        }

        return eventId;
    };

    /**
     * Evento Purchase - Cuando se confirma la compra (redundante con backend)
     */
    const trackPurchase = (params: {
        eventId: string; // Requerido para deduplicación con backend
        value: number;
        currency?: string;
        contentIds?: string[];
        orderId?: string;
    }) => {
        if (!isPixelLoaded()) return null;

        window.fbq('track', 'Purchase', {
            value: params.value,
            currency: params.currency || 'ARS',
            content_ids: params.contentIds || [],
            content_type: 'product',
            order_id: params.orderId,
        }, { eventID: params.eventId });

        return params.eventId;
    };

    /**
     * Evento AddToCart - Cuando el usuario agrega entradas al carrito
     */
    const trackAddToCart = (params: {
        eventId?: string;
        contentId: string;
        contentName: string;
        value: number;
        currency?: string;
    }) => {
        const eventId = params.eventId || generateEventId('atc');

        if (isPixelLoaded()) {
            window.fbq('track', 'AddToCart', {
                content_ids: [params.contentId],
                content_name: params.contentName,
                content_type: 'product',
                value: params.value,
                currency: params.currency || 'ARS',
            }, { eventID: eventId });
        }

        return eventId;
    };

    return {
        generateEventId,
        trackViewContent,
        trackInitiateCheckout,
        trackPurchase,
        trackAddToCart,
        isPixelLoaded,
    };
}

export default useMetaPixel;
